import { FilterField, filterControlClass } from '@/components/admin/versioned/filter-field';
import { useIsVitrine } from '@/lib/use-instance';
import type { VersionedWording } from '@/lib/versioned-labels';
import { REFERENT_NOWHERE, TAG_FILTER_LABEL } from '@/lib/versioned-labels';
import type { VersionedFilters, VersionedTagFilter } from '@matter_extractor/shared';
import { versionedTagFilterSchema } from '@matter_extractor/shared';
import { SlidersHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';
import { useId } from 'react';

/**
 * The five filters that are ALWAYS visible, and the door to the others
 * (refonte modulaire §4; maquette `Main.dc.html`).
 *
 * The five are common to every versioned object, which is why this row knows
 * none of them by name beyond what `versionedFiltersShape` declares. What sits
 * behind the button is the panel, and that is where an object's own filters
 * live.
 *
 * §4 fixes both the five and the split: « les cinq premiers sont toujours
 * visibles, les autres derrière "Plus de filtres" ». The five are the ones an
 * operator reaches for without thinking - a name, a tag, who uses it, who wrote
 * it - and the panel holds the ones that answer a question somebody came with.
 *
 * ## « Plus de filtres » is pushed to the RIGHT edge
 *
 * `ml-auto`, and the maquette draws it there deliberately: the button is not a
 * sixth filter, it is the boundary of the row. Sitting next to « Auteur » it
 * would read as one more thing to choose; at the far edge it reads as « there
 * is more ». It carries `aria-expanded` because that is what makes the state
 * legible to somebody who cannot see the panel move.
 *
 * ## The referent selects have two options and admit it
 *
 * « Tous » and « Nulle part », and the reason is at `REFERENT_NOWHERE`: a card
 * says usage as a count per kind, no route serves the referent list yet, and
 * nothing cites anything until the protocol lot lands. A select full of names
 * the page cannot resolve would empty the grid silently.
 *
 * ## And why an object may REPLACE those two (issue #55)
 *
 * The two middle slots hold the referent filters of §4 - « qui m'utilise » -
 * and for three of the four objects that is what an operator asks. For an
 * EXPERIENCE it is not: its referents are the links of §5.1 (§4, H25), so a
 * « Protocole : nulle part » on that screen would filter on a relation nothing
 * can have, and an « Expérience » select on the Expériences library reads as a
 * bug. §15 answers by naming the two visible slots of that screen « Protocole »
 * and « Persona » - the two references an experience CARRIES, the other
 * direction of the same arrow - and the mockup (`Main.dc.html`) draws them
 * there.
 *
 * So `renderReferents` REPLACES the pair, and nothing else: the name, the tag,
 * the author and « Plus de filtres » stay the layer's, in the same order, at the
 * same widths. Optional, and falling back to the two common selects, so the
 * three screens that came before emit the same nodes they always did.
 *
 * ## The casts on `onChange`
 *
 * `{ name: '...' }` is a patch of the COMMON filters, and every object's filter
 * type carries those thirteen fields - but TypeScript cannot see that through
 * an unresolved type parameter, so each patch is widened once. It pays for the
 * generic and for nothing an object could disagree about.
 */

export interface FilterBarProps<Filters extends VersionedFilters> {
  wording: VersionedWording;
  filters: Filters;
  /** Every author the list offers - « a créé ou a modifié », one filter (§4). */
  authors: readonly string[];
  expanded: boolean;
  onToggleExpanded: () => void;
  /**
   * The two middle selects, when the object's are not the referents of §4.
   *
   * Absent: « Protocole » and « Expérience », the two referent filters every
   * versioned object carries. See the head for why an experience replaces them.
   */
  renderReferents?: ReactNode;
  /** One dimension at a time; the caller merges and writes the URL. */
  onChange: (patch: Partial<Filters>) => void;
}

export function FilterBar<Filters extends VersionedFilters>({
  wording,
  filters,
  authors,
  expanded,
  onToggleExpanded,
  renderReferents,
  onChange,
}: FilterBarProps<Filters>) {
  const nameId = useId();
  const tagId = useId();
  const protocolId = useId();
  const experienceId = useId();
  const authorId = useId();
  // No « Tag » on the vitrine: it has one state, so there is nothing to filter by.
  const vitrine = useIsVitrine();

  return (
    <div className="flex flex-wrap items-end gap-2.5">
      <FilterField label="Nom" htmlFor={nameId} className="w-55">
        {/*
          CONTROLLED, and typing filters as it goes - unlike the reference
          search of `/admin/entretiens`, which submits. The difference is where
          the answer comes from: that screen asks the server per query, this one
          holds the whole library in one cache entry (one list schema), so
          a keystroke costs a `filter()` and not a round trip.
        */}
        <input
          id={nameId}
          data-testid={`${wording.entity}-filtre-nom`}
          type="search"
          value={filters.name}
          placeholder="Rechercher"
          onChange={(event) => onChange({ name: event.currentTarget.value } as Partial<Filters>)}
          className={filterControlClass(filters.name !== '', 'w-full')}
        />
      </FilterField>

      {vitrine ? null : (
        <FilterField label="Tag" htmlFor={tagId} className="w-40">
          <select
            id={tagId}
            data-testid={`${wording.entity}-filtre-tag`}
            value={filters.tag}
            onChange={(event) =>
              onChange({ tag: event.currentTarget.value as VersionedTagFilter } as Partial<Filters>)
            }
            className={filterControlClass(filters.tag !== 'all', 'w-full')}
          >
            {versionedTagFilterSchema.options.map((option) => (
              <option key={option} value={option}>
                {TAG_FILTER_LABEL[option]}
              </option>
            ))}
          </select>
        </FilterField>
      )}

      {renderReferents ?? (
        <>
          <FilterField label="Protocole" htmlFor={protocolId} className="w-45">
            <select
              id={protocolId}
              data-testid={`${wording.entity}-filtre-protocole`}
              value={filters.protocolId ?? ''}
              onChange={(event) =>
                onChange({
                  protocolId: event.currentTarget.value === '' ? null : REFERENT_NOWHERE,
                } as Partial<Filters>)
              }
              className={filterControlClass(filters.protocolId !== null, 'w-full')}
            >
              <option value="">Tous</option>
              <option value={REFERENT_NOWHERE}>Nulle part</option>
            </select>
          </FilterField>

          <FilterField label="Expérience" htmlFor={experienceId} className="w-45">
            <select
              id={experienceId}
              data-testid={`${wording.entity}-filtre-experience`}
              value={filters.experienceId ?? ''}
              onChange={(event) =>
                onChange({
                  experienceId: event.currentTarget.value === '' ? null : REFERENT_NOWHERE,
                } as Partial<Filters>)
              }
              className={filterControlClass(filters.experienceId !== null, 'w-full')}
            >
              <option value="">Toutes</option>
              <option value={REFERENT_NOWHERE}>Nulle part</option>
            </select>
          </FilterField>
        </>
      )}

      <FilterField label="Auteur" htmlFor={authorId} className="w-45">
        <select
          id={authorId}
          data-testid={`${wording.entity}-filtre-auteur`}
          value={filters.author ?? ''}
          onChange={(event) =>
            onChange({
              author: event.currentTarget.value === '' ? null : event.currentTarget.value,
            } as Partial<Filters>)
          }
          className={filterControlClass(filters.author !== null, 'w-full')}
        >
          <option value="">Tous</option>
          {authors.map((author) => (
            <option key={author} value={author}>
              {author}
            </option>
          ))}
        </select>
      </FilterField>

      <button
        type="button"
        data-testid={`${wording.entity}-plus-de-filtres`}
        aria-expanded={expanded}
        onClick={onToggleExpanded}
        className={`ml-auto inline-flex h-9.5 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 border-bleu-nuit px-3 font-semibold text-bleu-nuit text-sm leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${expanded ? 'bg-bleu-nuit-tint' : 'bg-white hover:bg-bleu-nuit-tint'}`}
      >
        <SlidersHorizontal aria-hidden="true" className="size-4" />
        <span>{expanded ? 'Moins de filtres' : 'Plus de filtres'}</span>
      </button>
    </div>
  );
}
